import { useEffect, useRef, useState } from "react";
import api from "../api";

const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`;

const STATE_LABEL = {
  authorized: { txt: "Collegato", cls: "gestita" },
  notAuthorized: { txt: "Da collegare (scansiona il QR)", cls: "da_gestire" },
  starting: { txt: "Avvio in corso…", cls: "da_gestire" },
  yellowCard: { txt: "Avviso WhatsApp (rallenta gli invii)", cls: "da_gestire" },
  blocked: { txt: "Bloccato", cls: "da_gestire" },
  notConfigured: { txt: "Non configurato", cls: "da_gestire" },
};

export default function GreenWhatsApp() {
  const [cfg, setCfg] = useState({ id_instance: "", api_token: "", api_url: "https://api.green-api.com" });
  const [configured, setConfigured] = useState(false);
  const [state, setState] = useState("notConfigured");
  const [qr, setQr] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const pollRef = useRef(null);

  const loadConfig = () => api.get("/whatsapp/config").then((r) => {
    setConfigured(r.data.configured);
    setCfg((c) => ({ ...c, id_instance: r.data.id_instance || "", api_url: r.data.api_url || c.api_url }));
  }).catch(() => {});
  const loadState = () => api.get("/whatsapp/state").then((r) => setState(r.data.stateInstance || "unknown")).catch(() => {});

  useEffect(() => { loadConfig(); loadState(); }, []);

  // polling stato + QR quando non collegato
  useEffect(() => {
    if (!configured) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = (await api.get("/whatsapp/state")).data.stateInstance;
        setState(s);
        if (s === "notAuthorized") {
          const q = (await api.get("/whatsapp/qr")).data;
          if (q.type === "qrCode" && q.message) setQr(`data:image/png;base64,${q.message}`);
          else setQr("");
        } else {
          setQr("");
        }
      } catch (err) { console.error("Errore polling stato WhatsApp:", err); }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [configured]);

  const saveAndConnect = async () => {
    if (!cfg.id_instance.trim() || !cfg.api_token.trim()) { setMsg("Inserisci idInstance e apiTokenInstance."); return; }
    setBusy(true); setMsg("Salvataggio e configurazione webhook…");
    try {
      await api.put("/whatsapp/config", { ...cfg, webhook_url: WEBHOOK_URL });
      setConfigured(true);
      setMsg("Configurato! Ora premi 'Mostra QR' e scansiona dal telefono (WhatsApp → Dispositivi collegati).");
      loadState();
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const showQr = async () => {
    setBusy(true); setMsg("");
    try {
      const q = (await api.get("/whatsapp/qr")).data;
      if (q.type === "qrCode" && q.message) setQr(`data:image/png;base64,${q.message}`);
      else if (q.type === "alreadyLogged") setMsg("Già collegato.");
      else setMsg(q.message || "QR non disponibile (verifica le credenziali).");
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const reconnect = async () => {
    setBusy(true); setMsg("Scollego l'istanza per ri-scansionare il QR…");
    try { await api.post("/whatsapp/logout"); setTimeout(() => { loadState(); showQr(); }, 1500); }
    catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const reboot = async () => {
    setBusy(true); setMsg("Riavvio istanza…");
    try { await api.post("/whatsapp/reboot"); setTimeout(loadState, 2000); setMsg("Riavviata. Lo stato si aggiorna tra poco."); }
    catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const lbl = STATE_LABEL[state] || { txt: state, cls: "da_gestire" };

  return (
    <div className="panel" data-testid="green-whatsapp-panel" style={{ borderLeft: "4px solid #25D366", marginBottom: 18 }}>
      <h3>📱 WhatsApp del telefono (collegamento via QR)</h3>
      <p className="muted">Collega il WhatsApp/WhatsApp Business del tuo cellulare per <strong>ricevere</strong> i messaggi nel CRM (finiscono in "Da gestire") e salvare automaticamente i contatti. Solo per ricevere e rispondere: <strong>non</strong> per invii massivi (per quelli usa SMShosting). Tecnologia: Green-API.</p>

      <div className="grid-2">
        <div className="field"><label>idInstance</label>
          <input value={cfg.id_instance} onChange={(e) => setCfg({ ...cfg, id_instance: e.target.value })} placeholder="es. 1101234567" data-testid="green-id-instance" />
        </div>
        <div className="field"><label>apiTokenInstance</label>
          <input type="password" value={cfg.api_token} onChange={(e) => setCfg({ ...cfg, api_token: e.target.value })} placeholder={configured ? "••••• (lascia per non modificare)" : ""} data-testid="green-api-token" />
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12 }}>Crea un'istanza gratuita su <strong>green-api.com</strong> (Console → Create instance) e copia qui idInstance e apiTokenInstance.</p>

      <div className="toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
        <button className="btn primary" onClick={saveAndConnect} disabled={busy} data-testid="green-save-btn">Salva e collega</button>
        {configured && <button className="btn" onClick={showQr} disabled={busy} data-testid="green-qr-btn">Mostra QR</button>}
        {configured && <button className="btn" onClick={reconnect} disabled={busy} data-testid="green-reconnect-btn">Ri-collega (logout + QR)</button>}
        {configured && <button className="btn" onClick={reboot} disabled={busy} data-testid="green-reboot-btn">Riavvia</button>}
        <div className="spacer" />
        <span className="muted">Stato:</span> <span className={"pill " + lbl.cls} data-testid="green-state">{lbl.txt}</span>
      </div>

      {qr && state !== "authorized" && (
        <div style={{ marginTop: 14, textAlign: "center" }} data-testid="green-qr-img">
          <img src={qr} alt="QR WhatsApp" style={{ width: 240, height: 240, background: "#fff", padding: 8, borderRadius: 12 }} />
          <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>Apri WhatsApp sul telefono → Impostazioni → Dispositivi collegati → Collega un dispositivo, e inquadra questo QR. Si aggiorna da solo.</p>
        </div>
      )}
      {msg && <p className="muted" data-testid="green-msg" style={{ marginTop: 10 }}>{msg}</p>}
    </div>
  );
}
